import { useState, cloneElement, useCallback, useRef } from 'react';
import { Button } from 'foundations-components/transitional/components/Button';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { Text } from 'foundations-components/components/Text';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { View } from 'foundations-components/components/View';
import { FeedbackSurveyResponses } from './internal/FeedbackSurveyResponses';
import { FeedbackSurveyCharacterCounter } from './internal/FeedbackSurveyCharacterCounter';
import { isTrellisTheme, useTheme } from 'foundations-theming/utils';
import { useTrackSurveyView, useHideSurvey } from '../utils/surveyUtils';
import { submitCsatSurvey } from '../api/surveyApi';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FeedbackSurveyModalContent = ({
  buttonText,
  footer,
  placeholder,
  rating = 2,
  subTitle,
  title,
  surveyConfig,
  surveyId,
  selectableResponses = [],
  textAreaLabel,
  onClose,
  onCloseComplete,
  onOpenStart,
  onOpenComplete,
  onSubmitComplete
}) => {
  const [followUp, setFollowUp] = useState();
  const selectedResponses = useRef(new Set());
  useHideSurvey(surveyConfig);
  useTrackSurveyView(surveyConfig);
  const theme = useTheme();
  const isTrellis = isTrellisTheme(theme);
  const handleSurveySubmit = useCallback(() => {
    if (followUp || selectedResponses.current.size > 0) {
      const {
        contactId
      } = surveyConfig.webRespondent || {};
      const selectedResponsesString = Array.from(selectedResponses.current).join(', ');

      // Concatenate follow up and selected responses following these rules:
      // If there is a follow up, and there are selected responses, add a separator: " - "
      // If there is a follow up, and there are no selected responses, add the follow up without a separator
      // If there is no follow up, and there are selected responses, add the selected responses without a separator
      const followUpString = `${followUp || ''}${followUp && selectedResponsesString.length > 0 ? ' - ' : ''}${selectedResponsesString || ''}`;

      // Voiding so we don't have to handle the error in this function
      // The error is already caught in `submitCsatSurvey`
      void submitCsatSurvey({
        surveyId,
        contactId,
        rating,
        followUp: followUpString
      }).finally(() => onSubmitComplete === null || onSubmitComplete === void 0 ? void 0 : onSubmitComplete(followUpString));
    }
    onClose();
  }, [surveyConfig, rating, followUp, onSubmitComplete, onClose, surveyId]);
  const handleSelectResponse = useCallback((response, isSelected) => {
    if (isSelected) {
      selectedResponses.current.add(response);
    } else {
      selectedResponses.current.delete(response);
    }
  }, []);
  return /*#__PURE__*/_jsxs(Modal, {
    "data-test-id": "feedback-survey-modal",
    onOpenStart: onOpenStart,
    onOpenComplete: onOpenComplete,
    onCloseComplete: onCloseComplete,
    width: 550,
    children: [isTrellis && /*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      }), /*#__PURE__*/_jsx(Text, {
        children: title
      })]
    }), /*#__PURE__*/_jsxs(DialogBody, {
      children: [!isTrellis && /*#__PURE__*/_jsxs(View, {
        paddingBlockStart: "200",
        paddingInlineEnd: "200",
        children: [/*#__PURE__*/_jsx(DialogCloseButton, {
          color: "icon-primary-disabled",
          onClick: onClose
        }), /*#__PURE__*/_jsx(Text, {
          as: "h2",
          typography: "heading-300",
          display: "block",
          paddingBlockEnd: "500",
          children: title
        })]
      }), subTitle && /*#__PURE__*/_jsx(Text, {
        as: "p",
        display: "block",
        textAlign: "start",
        children: subTitle
      }), selectableResponses.length > 0 && /*#__PURE__*/_jsx(FeedbackSurveyResponses, {
        onSelectResponse: handleSelectResponse,
        responses: selectableResponses
      }), /*#__PURE__*/_jsx(FeedbackSurveyCharacterCounter, {
        placeholder: placeholder,
        setFollowUp: setFollowUp,
        label: textAreaLabel
      })]
    }), /*#__PURE__*/_jsx(DialogFooter, {
      paddingBlockStart: "0",
      children: footer ? ( /*#__PURE__*/cloneElement(footer, {
        followUp,
        onSubmitComplete,
        surveyConfig
      })) : /*#__PURE__*/_jsx(Button, {
        onClick: handleSurveySubmit,
        size: "sm",
        use: "primary",
        children: buttonText
      })
    })]
  });
};
export default FeedbackSurveyModalContent;